import React, { useState, useRef, useEffect } from 'react';
import { 
  User, Phone, Mail, Globe, CreditCard, FileText, 
  Package, Image, Tag, Send, ChevronRight, ChevronLeft, 
  Check, Upload, X, Hash, MapPin, Banknote, Shield,
  Gift, ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import './VendorOnboarding.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Pollen mascot image
const POLLEN_BEE_IMG = "https://customer-assets.emergentagent.com/job_bee-booking/artifacts/k8zgjyqb_Pollen%20-%20bHyyve%20Mascot.png";

// Pollen's encouraging statements for onboarding steps
const POLLEN_ENCOURAGEMENTS = [
  "You're doing amazing! Every successful business starts with a single step! 🐝",
  "I believe in you! Your business is going to thrive on bHyyve! ✨",
  "You've got this! Soon you'll be connecting with buyers across Ontario! 💪",
  "Keep going! Great things are coming your way! 🌟",
  "Fantastic progress! Your future customers are waiting! 🎯",
  "You're building something special! I can feel it! 🚀",
  "Almost there! Success is just around the corner! 🏆",
  "Your dedication inspires me! Let's make magic happen! ✨",
  "Every detail matters - you're creating your success story! 📖",
  "I'm so excited for your journey with bHyyve! Let's do this! 🎉",
  "Your entrepreneurial spirit is amazing! Keep shining! ⭐",
  "The bHyyve community is lucky to have you joining! 🐝",
  "Dreams become reality one step at a time - you're doing it! 🌈"
];

// Ontario cities for autocomplete
const ONTARIO_CITIES = [
  "Ajax", "Aurora", "Barrie", "Belleville", "Brampton", "Brantford", "Brockville",
  "Burlington", "Cambridge", "Chatham-Kent", "Clarington", "Cornwall", "East Gwillimbury",
  "Etobicoke", "Georgetown", "Georgina", "Grimsby", "Guelph", "Halton Hills", "Hamilton",
  "Innisfil", "Kawartha Lakes", "Kenora", "Kingston", "Kitchener", "Lincoln", "London",
  "Markham", "Milton", "Mississauga", "Newmarket", "Niagara Falls", "North Bay",
  "North York", "Oakville", "Orangeville", "Orillia", "Oshawa", "Ottawa", "Owen Sound",
  "Peterborough", "Pickering", "Port Colborne", "Prince Edward County", "Richmond Hill",
  "Sarnia", "Sault Ste. Marie", "Scarborough", "St. Catharines", "St. Thomas", "Stouffville",
  "Stratford", "Sudbury", "Thunder Bay", "Timmins", "Toronto", "Vaughan", "Waterloo",
  "Welland", "Whitby", "Whitchurch-Stouffville", "Windsor", "Woodbridge", "Woodstock"
];

const steps = [
  { id: 1, title: "Your Name", icon: User, field: "firstName", type: "name", placeholder: "First Name", secondField: "lastName", secondPlaceholder: "Last Name" },
  { id: 2, title: "Phone Number", icon: Phone, field: "phone", type: "tel", placeholder: "+1 (555) 000-0000" },
  { id: 3, title: "Email Address", icon: Mail, field: "email", type: "email", placeholder: "you@business.com" },
  { id: 4, title: "Business Website", icon: Globe, field: "website", type: "url", placeholder: "https://yourbusiness.com" },
  { id: 5, title: "HST Number", icon: Hash, field: "hstNumber", type: "text", placeholder: "123456789RT0001", optional: true, description: "Optional - Enter your HST registration number" },
  { id: 6, title: "Service Cities", icon: MapPin, field: "serviceCities", type: "cities", description: "Add the cities in Ontario where you provide services" },
  { id: 7, title: "Payment Method", icon: Banknote, field: "paymentMethod", type: "payment", description: "How would you like to receive payments?" },
  { id: 8, title: "Photo ID", icon: CreditCard, field: "photoId", type: "file", description: "Upload driver's license or photo ID (not health card)", accept: "image/*,.pdf" },
  { id: 9, title: "Business Registration", icon: FileText, field: "businessDocs", type: "file", description: "Upload business registration documents matching your photo ID", accept: "image/*,.pdf" },
  { id: 10, title: "Product List", icon: Package, field: "productList", type: "file", description: "Upload Excel file with: Product name, price, image (if applicable), description", accept: ".xlsx,.xls,.csv" },
  { id: 11, title: "Company Logo", icon: Image, field: "logo", type: "file", description: "Upload your company logo", accept: "image/*" },
  { id: 12, title: "Current Promos", icon: Tag, field: "promos", type: "textarea", placeholder: "List your current promotions, discounts, or special offers..." },
  { id: 13, title: "Submit Application", icon: Send, field: "submit", type: "submit" }
];

const VendorOnboarding = () => {
  // Page states
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    serviceCities: [],
    paymentMethod: ''
  });
  const [completedSteps, setCompletedSteps] = useState([]);
  const [beeFlipping, setBeeFlipping] = useState(false);
  const [showSparklers, setShowSparklers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  
  // Pollen interaction states
  const [currentEncouragement, setCurrentEncouragement] = useState('');
  
  // City autocomplete state
  const [cityInput, setCityInput] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  // Update encouragement when step changes
  useEffect(() => {
    if (!submitted) {
      const randomIndex = Math.floor(Math.random() * POLLEN_ENCOURAGEMENTS.length);
      setCurrentEncouragement(POLLEN_ENCOURAGEMENTS[randomIndex]);
    }
  }, [currentStep, submitted]);

  const triggerBeeFlip = () => {
    setBeeFlipping(true);
    setShowSparklers(true);
    setTimeout(() => setBeeFlipping(false), 1200);
    setTimeout(() => setShowSparklers(false), 2000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, [field]: file }));
    }
  };
  
  // City input handlers
  const handleCityInputChange = (value) => {
    setCityInput(value);
    if (value.length > 0) {
      const filtered = ONTARIO_CITIES.filter(city => 
        city.toLowerCase().includes(value.toLowerCase()) &&
        !formData.serviceCities.includes(city)
      );
      setCitySuggestions(filtered.slice(0, 8));
      setShowCitySuggestions(true);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };
  
  const addCity = (city) => {
    if (!formData.serviceCities.includes(city)) {
      setFormData(prev => ({
        ...prev,
        serviceCities: [...prev.serviceCities, city]
      }));
    }
    setCityInput('');
    setCitySuggestions([]);
    setShowCitySuggestions(false);
  };
  
  const removeCity = (city) => {
    setFormData(prev => ({
      ...prev,
      serviceCities: prev.serviceCities.filter(c => c !== city)
    }));
  };
  
  const handleCityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (citySuggestions.length > 0) {
        addCity(citySuggestions[0]);
      } else if (cityInput.trim()) {
        addCity(cityInput.trim());
      }
    }
  };

  const isStepValid = () => {
    const step = steps[currentStep];
    if (step.type === 'submit') return true;
    if (step.optional) return true;
    if (step.type === 'file') return !!formData[step.field];
    if (step.type === 'name') {
      return formData[step.field]?.trim?.() !== '' && 
             formData[step.field] !== undefined &&
             formData[step.secondField]?.trim?.() !== '' && 
             formData[step.secondField] !== undefined;
    }
    if (step.type === 'cities') {
      return formData.serviceCities && formData.serviceCities.length > 0;
    }
    if (step.type === 'payment') {
      if (!formData.paymentMethod) return false;
      if (formData.paymentMethod === 'interac') {
        return formData.interacEmail?.trim?.() !== '' && formData.interacEmail !== undefined;
      }
      if (formData.paymentMethod === 'direct_deposit') {
        return formData.transitNumber?.trim?.() !== '' && 
               formData.institutionNumber?.trim?.() !== '' && 
               formData.accountNumber?.trim?.() !== '';
      }
      return false;
    }
    return formData[step.field]?.trim?.() !== '' && formData[step.field] !== undefined;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const step = steps[currentStep];
      if (step.type !== 'submit' && step.type !== 'textarea' && isStepValid()) {
        nextStep();
      }
    }
  };

  useEffect(() => {
    if (!submitted) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [submitted, currentStep, formData]);

  const nextStep = () => {
    if (!isStepValid()) {
      toast.error('Please complete this step before continuing');
      return;
    }

    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
      triggerBeeFlip();
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      
      const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
      submitData.append('name', fullName);
      submitData.append('phone', formData.phone || '');
      submitData.append('email', formData.email || '');
      submitData.append('website', formData.website || '');
      submitData.append('promos', formData.promos || '');
      submitData.append('password', 'bHyyve2026');
      
      submitData.append('hstNumber', formData.hstNumber || '');
      submitData.append('serviceCities', JSON.stringify(formData.serviceCities || []));
      submitData.append('paymentMethod', formData.paymentMethod || '');
      
      if (formData.paymentMethod === 'interac') {
        submitData.append('interacEmail', formData.interacEmail || '');
      } else if (formData.paymentMethod === 'direct_deposit') {
        submitData.append('transitNumber', formData.transitNumber || '');
        submitData.append('institutionNumber', formData.institutionNumber || '');
        submitData.append('accountNumber', formData.accountNumber || '');
      }
      
      if (formData.photoId) submitData.append('photoId', formData.photoId);
      if (formData.businessDocs) submitData.append('businessDocs', formData.businessDocs);
      if (formData.productList) submitData.append('productList', formData.productList);
      if (formData.logo) submitData.append('logo', formData.logo);
      
      const response = await fetch(`${API_URL}/api/vendor-onboarding`, {
        method: 'POST',
        body: submitData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit application');
      }
      
      setCompletedSteps(prev => [...prev, currentStep]);
      triggerBeeFlip();
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Stripe Payment Link - UPDATE THIS URL when you have your actual Stripe Payment Link
  const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/PLACEHOLDER_UPDATE_THIS";

  // Success Screen
  if (submitted) {
    return (
      <div className="onboarding-page success-page" data-testid="success-page">
        <div className="sparklers-container success-sparklers">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="sparkle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${1 + Math.random() * 2}s`
            }} />
          ))}
        </div>
        <div className="success-container">
          <div className="success-bee-container">
            <div className="success-bee">
              <img src={POLLEN_BEE_IMG} alt="Pollen the bee" className="pollen-bee-img success-pollen" />
            </div>
          </div>
          <h1 className="success-title" data-testid="success-title">Application Submitted!</h1>
          <p className="success-message">
            Thank you for applying to become a bHyyve vendor! Complete your payment below to activate your Founder's Membership.
          </p>
          
          {/* Payment Section */}
          <div className="payment-section" data-testid="payment-section">
            <div className="payment-card">
              <div className="payment-header">
                <Gift size={28} className="payment-gift-icon" />
                <h2>Complete Your Payment</h2>
              </div>
              <div className="payment-details-box">
                <div className="payment-line-item">
                  <span>Vendor Membership (1 Year)</span>
                  <span>$29.00</span>
                </div>
                <div className="payment-line-item">
                  <span>HST (13%)</span>
                  <span>$3.77</span>
                </div>
                <div className="payment-line-item payment-total">
                  <span>Total</span>
                  <span>$32.77 CAD</span>
                </div>
              </div>
              <a 
                href={STRIPE_PAYMENT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="pay-now-btn"
                data-testid="stripe-payment-btn"
              >
                <CreditCard size={20} />
                Pay Now - $32.77 CAD
                <ArrowRight size={20} />
              </a>
              <div className="payment-trust">
                <Shield size={16} />
                <span>Secure payment powered by Stripe</span>
              </div>
            </div>
            <p className="payment-note">
              Your application will be reviewed within 2-3 business days. Payment secures your Founder's pricing.
            </p>
          </div>
          
          <a href="/" className="back-home-link" data-testid="back-home-btn">
            ← Back to Home
          </a>
        </div>
      </div>
    );
  }

  const step = steps[currentStep];
  const StepIcon = step.icon;
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Onboarding Steps
  return (
    <div className="onboarding-page" data-testid="onboarding-steps-page">
      {/* Sparklers Animation */}
      {showSparklers && (
        <div className="sparklers-container">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="sparkle" style={{
              left: `${20 + Math.random() * 60}%`,
              top: `${10 + Math.random() * 40}%`,
              animationDelay: `${Math.random() * 0.5}s`
            }} />
          ))}
        </div>
      )}

      {/* Happy Flying Bee with Encouragement */}
      <div className={`flying-bee ${beeFlipping ? 'flipping' : ''}`}>
        <div className="bee-character">
          <img src={POLLEN_BEE_IMG} alt="Pollen the bee" className="pollen-bee-img flying-pollen" />
        </div>
        {/* Pollen Encouragement Bubble */}
        <div className="pollen-encouragement-bubble" data-testid="pollen-encouragement">
          <p>{currentEncouragement}</p>
        </div>
      </div>

      {/* Progress */}
      <div className="onboarding-progress">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${progress}%` }} data-testid="progress-bar"></div>
        </div>
        <p className="progress-label" data-testid="progress-label">Step {currentStep + 1} of {steps.length}</p>
      </div>

      {/* Step Indicators */}
      <div className="step-indicators" data-testid="step-indicators">
        {steps.map((s, index) => (
          <div 
            key={s.id} 
            className={`step-dot ${index === currentStep ? 'active' : ''} ${completedSteps.includes(index) ? 'completed' : ''}`}
            data-testid={`step-dot-${index + 1}`}
          >
            {completedSteps.includes(index) ? <Check size={12} /> : index + 1}
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="onboarding-content">
        <div className="step-card" data-testid="step-card">
          {/* Step Icon */}
          <div className="step-icon-container">
            <div className="step-icon">
              <StepIcon size={64} />
            </div>
          </div>

          {/* Step Title */}
          <h2 className="step-title" data-testid="step-title">{step.title}</h2>
          
          {/* Step Description */}
          {step.description && (
            <p className="step-description">{step.description}</p>
          )}

          {/* Step Input */}
          <div className="step-input-container">
            {step.type === 'name' ? (
              <div className="name-inputs-container">
                <Input
                  type="text"
                  value={formData[step.field] || ''}
                  onChange={(e) => handleInputChange(step.field, e.target.value)}
                  placeholder={step.placeholder}
                  className="step-input"
                  data-testid={`input-${step.field}`}
                />
                <Input
                  type="text"
                  value={formData[step.secondField] || ''}
                  onChange={(e) => handleInputChange(step.secondField, e.target.value)}
                  placeholder={step.secondPlaceholder}
                  className="step-input"
                  data-testid={`input-${step.secondField}`}
                />
              </div>
            ) : step.type === 'text' || step.type === 'tel' || step.type === 'email' || step.type === 'url' ? (
              <Input
                type={step.type}
                value={formData[step.field] || ''}
                onChange={(e) => handleInputChange(step.field, e.target.value)}
                placeholder={step.placeholder}
                className="step-input"
                data-testid={`input-${step.field}`}
              />
            ) : step.type === 'textarea' ? (
              <textarea
                value={formData[step.field] || ''}
                onChange={(e) => handleInputChange(step.field, e.target.value)}
                placeholder={step.placeholder}
                className="step-textarea"
                rows={5}
                data-testid={`textarea-${step.field}`}
              />
            ) : step.type === 'cities' ? (
              <div className="cities-input-container" data-testid="cities-input">
                <div className="city-search-wrapper">
                  <Input
                    type="text"
                    value={cityInput}
                    onChange={(e) => handleCityInputChange(e.target.value)}
                    onKeyDown={handleCityKeyDown}
                    placeholder="Type a city name and press Enter..."
                    className="step-input city-input"
                    data-testid="city-search-input"
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="city-suggestions" data-testid="city-suggestions">
                      {citySuggestions.map(city => (
                        <button
                          key={city}
                          type="button"
                          className="city-suggestion-item"
                          onClick={() => addCity(city)}
                        >
                          <MapPin size={14} />
                          {city}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {formData.serviceCities && formData.serviceCities.length > 0 && (
                  <div className="selected-cities" data-testid="selected-cities">
                    {formData.serviceCities.map(city => (
                      <span key={city} className="city-tag">
                        {city}
                        <button 
                          type="button" 
                          onClick={() => removeCity(city)}
                          className="city-remove-btn"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : step.type === 'payment' ? (
              <div className="payment-method-container" data-testid="payment-method">
                <div className="payment-options">
                  <button
                    type="button"
                    className={`payment-option ${formData.paymentMethod === 'interac' ? 'selected' : ''}`}
                    onClick={() => handleInputChange('paymentMethod', 'interac')}
                    data-testid="payment-interac"
                  >
                    <div className="payment-option-icon">
                      <Mail size={24} />
                    </div>
                    <span>Interac e-Transfer</span>
                  </button>
                  <button
                    type="button"
                    className={`payment-option ${formData.paymentMethod === 'direct_deposit' ? 'selected' : ''}`}
                    onClick={() => handleInputChange('paymentMethod', 'direct_deposit')}
                    data-testid="payment-direct-deposit"
                  >
                    <div className="payment-option-icon">
                      <Banknote size={24} />
                    </div>
                    <span>Direct Deposit</span>
                  </button>
                </div>
                
                {formData.paymentMethod === 'interac' && (
                  <div className="payment-details interac-details" data-testid="interac-details">
                    <label className="payment-label">Interac e-Transfer Email</label>
                    <Input
                      type="email"
                      value={formData.interacEmail || ''}
                      onChange={(e) => handleInputChange('interacEmail', e.target.value)}
                      placeholder="your@email.com"
                      className="step-input"
                      data-testid="interac-email-input"
                    />
                  </div>
                )}
                
                {formData.paymentMethod === 'direct_deposit' && (
                  <div className="payment-details deposit-details" data-testid="deposit-details">
                    <div className="deposit-field">
                      <label className="payment-label">Transit Number (5 digits)</label>
                      <Input
                        type="text"
                        value={formData.transitNumber || ''}
                        onChange={(e) => handleInputChange('transitNumber', e.target.value.replace(/\D/g, '').slice(0, 5))}
                        placeholder="12345"
                        maxLength={5}
                        className="step-input"
                        data-testid="transit-number-input"
                      />
                    </div>
                    <div className="deposit-field">
                      <label className="payment-label">Institution Number (3 digits)</label>
                      <Input
                        type="text"
                        value={formData.institutionNumber || ''}
                        onChange={(e) => handleInputChange('institutionNumber', e.target.value.replace(/\D/g, '').slice(0, 3))}
                        placeholder="004"
                        maxLength={3}
                        className="step-input"
                        data-testid="institution-number-input"
                      />
                    </div>
                    <div className="deposit-field">
                      <label className="payment-label">Account Number (7 digits)</label>
                      <Input
                        type="text"
                        value={formData.accountNumber || ''}
                        onChange={(e) => handleInputChange('accountNumber', e.target.value.replace(/\D/g, '').slice(0, 7))}
                        placeholder="1234567"
                        maxLength={7}
                        className="step-input"
                        data-testid="account-number-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : step.type === 'file' ? (
              <div className={`file-upload-container ${formData[step.field] ? 'has-file' : ''}`} data-testid={`file-upload-${step.field}`}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileChange(step.field, e)}
                  accept={step.accept}
                  className="file-input-hidden"
                  id={`file-${step.field}`}
                  data-testid={`file-input-${step.field}`}
                />
                <label htmlFor={`file-${step.field}`} className="file-upload-label">
                  {formData[step.field] ? <Check size={32} /> : <Upload size={32} />}
                  <span>{formData[step.field]?.name || 'Click to upload file'}</span>
                </label>
                {formData[step.field] && (
                  <button 
                    className="file-clear-btn"
                    onClick={() => setFormData(prev => ({ ...prev, [step.field]: null }))}
                    data-testid={`clear-${step.field}-btn`}
                  >
                    <X size={16} /> Clear
                  </button>
                )}
              </div>
            ) : step.type === 'submit' ? (
              <div className="submit-container" data-testid="submit-container">
                <p className="submit-message">
                  You're almost done! Review your information and submit your application.
                </p>
                <div className="review-summary" data-testid="review-summary">
                  {steps.slice(0, -1).map((s, index) => (
                    <div key={s.id} className="review-item" data-testid={`review-item-${s.field}`}>
                      <span className="review-label">{s.title}:</span>
                      <span className="review-value">
                        {s.type === 'file' 
                          ? (formData[s.field]?.name || 'Not uploaded')
                          : s.type === 'name'
                          ? (`${formData[s.field] || ''} ${formData[s.secondField] || ''}`.trim() || 'Not provided')
                          : s.type === 'cities'
                          ? (formData.serviceCities?.length > 0 ? formData.serviceCities.join(', ') : 'Not provided')
                          : s.type === 'payment'
                          ? (formData.paymentMethod === 'interac' 
                              ? `Interac e-Transfer (${formData.interacEmail || 'No email'})` 
                              : formData.paymentMethod === 'direct_deposit'
                              ? 'Direct Deposit'
                              : 'Not selected')
                          : (formData[s.field] || (s.optional ? 'Not provided (optional)' : 'Not provided'))
                        }
                      </span>
                      {completedSteps.includes(index) && <Check size={16} className="review-check" />}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* Navigation */}
          <div className="step-navigation">
            <Button
              onClick={prevStep}
              disabled={currentStep === 0}
              className="nav-btn prev-btn"
              data-testid="prev-step-btn"
            >
              <ChevronLeft size={20} />
              Back
            </Button>
            
            {step.type === 'submit' ? (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="nav-btn submit-btn"
                data-testid="submit-application-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
                <Send size={20} />
              </Button>
            ) : (
              <Button
                onClick={nextStep}
                disabled={!isStepValid()}
                className="nav-btn next-btn"
                data-testid="next-step-btn"
              >
                Continue
                <ChevronRight size={20} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorOnboarding;
